import { Link, Upload } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthProvider";
import { BASE_URL } from '../apiConfig';
import { toast } from 'sonner';
import Shopify from '../images/Shopify.png';
import YouCan from '../images/youcan.png';
import Woocommerce from '../images/woocommerce.png';

export default function IntegrationAppCard({ app, fetchApps }) {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showShopInput, setShowShopInput] = useState(false);
  const [shopDomain, setShopDomain] = useState('');
  const [showWooModal, setShowWooModal] = useState(false);
  const [wooCredentials, setWooCredentials] = useState(null);
  const [showSendProductsModal, setShowSendProductsModal] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [availableProducts, setAvailableProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [productLastPage, setProductLastPage] = useState(1);
  const [productsTotal, setProductsTotal] = useState(0);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [isSyncingProducts, setIsSyncingProducts] = useState(false);

  const productSearchDebounceRef = useRef(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const shop = urlParams.get('shop');
    const host = urlParams.get('host');
    const hmac = urlParams.get('hmac');
    
    if (code && shop) {
      handleOAuthCallback({ code, shop, host, hmac });
    }
  }, []);

  const handleOAuthCallback = async (params) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/integrations/oauth/callback?${new URLSearchParams(params)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      const result = await response.json();

      if (result.success) {
        await fetchApps();
        
        toast.success('Connexion réussie ! Chargement de l\'application...');
        
        if (result.embedded) {
          redirectToShopifyApp(result.redirect_url, result.host);
        } else {
          window.location.href = result.redirect_url;
        }
      } else {
        toast.error(result.message || 'Échec de la connexion');
      }
    } catch (error) {
      console.error('Error in OAuth callback:', error);
      toast.error('Une erreur est survenue lors de la connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const redirectToShopifyApp = (redirectUrl, host) => {
    if (window.shopify && window.shopify.redirect) {
      window.shopify.redirect({
        target: 'app',
        url: redirectUrl
      });
    } else {
      if (window.top) {
        window.top.location.href = redirectUrl;
      } else {
        window.location.href = redirectUrl;
      }
    }
  };

  const handleWooCommerce = (result) => {
    setWooCredentials(result);
    setShowWooModal(true);
    fetchApps();
    toast.success('Clés WooCommerce créées avec succès');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers !');
  };

  const initiateOAuth = async () => {
    setIsLoading(true);
    try {
      const requestBody = { 
        app_name: app.name 
      };
      
      if (app.name === 'Shopify' && shopDomain.trim()) {
        requestBody.shop_domain = shopDomain.trim();
      }

      const response = await fetch(`${BASE_URL}/api/integrations/oauth/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        if (result.is_woocommerce) {
          handleWooCommerce(result);
        } else if (result.oauth_url) {
          sessionStorage.setItem('shopify_oauth_state', JSON.stringify({
            shop: shopDomain,
            timestamp: Date.now()
          }));
          
          window.location.href = result.oauth_url;
        }
      } else {
        if (result.requires_shop_domain) {
          setShowShopInput(true);
          toast.error('Veuillez entrer le domaine de votre boutique Shopify');
        } else {
          toast.error(result.message || 'Échec de la connexion');
        }
      }
    } catch (error) {
      console.error('Error connecting:', error);
      toast.error('Échec de la connexion. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = () => {
    if (app.name === 'Shopify' && !app.access_token) {
      setShowShopInput(true);
      return;
    }
    initiateOAuth();
  };

  const openSendProductsModal = async () => {
    setShowSendProductsModal(true);
    setProductSearch('');
    setProductPage(1);
    setProductLastPage(1);
    setProductsTotal(0);
    setSelectedProductIds([]);
    if (productSearchDebounceRef.current) {
      clearTimeout(productSearchDebounceRef.current);
    }
    await fetchProductsForSync('', 1);
  };

  const fetchProductsForSync = async (search = '', page = 1) => {
    setIsLoadingProducts(true);
    try {
      const response = await fetch(`${BASE_URL}/api/getproducts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          category: '',
          page,
          search,
          sort: 'newest',
        }),
      });

      const result = await response.json();

      const paginator =
        result?.data && Array.isArray(result?.data?.data) ? result.data : null;
      const rows = paginator
        ? paginator.data
        : Array.isArray(result?.data)
          ? result.data
          : [];

      setAvailableProducts(Array.isArray(rows) ? rows : []);
      setProductPage(Number(paginator?.current_page ?? page));
      setProductLastPage(Number(paginator?.last_page ?? 1));
      setProductsTotal(
        Number(paginator?.total ?? (Array.isArray(rows) ? rows.length : 0))
      );
    } catch (error) {
      console.error('Error loading products for sync:', error);
      toast.error('Impossible de charger les produits');
      setAvailableProducts([]);
      setProductPage(1);
      setProductLastPage(1);
      setProductsTotal(0);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleProductPageChange = (nextPage) => {
    if (typeof nextPage !== "number") return;
    if (nextPage < 1 || nextPage > productLastPage) return;
    setProductPage(nextPage);
    fetchProductsForSync(productSearch, nextPage);
  };

  const getVisibleProductPages = () => {
    const totalPages = productLastPage;
    if (totalPages <= 1) return [1];

    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, productPage - delta);
      i <= Math.min(totalPages - 1, productPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (productPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (productPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const toggleProductSelection = (productId) => {
    setSelectedProductIds((prev) => (
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    ));
  };

  const handleSyncSelectedProducts = async () => {
    if (selectedProductIds.length === 0) {
      toast.error('Sélectionnez au moins un produit');
      return;
    }

    setIsSyncingProducts(true);
    try {
      const endpoint = app.name === 'YouCan' 
        ? `${BASE_URL}/api/products/sync-youcan/bulk` 
        : `${BASE_URL}/api/products/sync-shopify/bulk`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_ids: selectedProductIds,
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Sync failed');
      }

      const successCount = (result.results || []).filter((r) => r.status === 'success').length;
      const failedCount = (result.results || []).filter((r) => r.status === 'failed').length;

      toast.success(`${successCount} produit(s) envoyé(s) à ${app.name}`);
      if (failedCount > 0) {
        toast.error(`${failedCount} produit(s) ont échoué`);
      }

      setShowSendProductsModal(false);
      setSelectedProductIds([]);
    } catch (error) {
      console.error('Error syncing selected products:', error);
      toast.error(error.message || `Échec de l’envoi vers ${app.name}`);
    } finally {
      setIsSyncingProducts(false);
    }
  };

  const maskHalf = (value) => {
    if (value === null || value === undefined) return null;
  
    const str = String(value);
  
    return str.length > 20 
      ? str.slice(0, 20) + '...' 
      : str;
  };

  const getAppLogo = (appName) => {
    if (appName === 'Shopify') return Shopify;
    if (appName === 'YouCan') return YouCan;
    if (appName === 'WooCommerce') return Woocommerce;
    return "/placeholder.svg";
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group hover:-translate-y-1">
        <div className="relative bg-gradient-to-br from-[var(--color-primary-50)] to-[var(--color-primary-50)] px-4 py-6 flex items-center justify-center">
          <div className="w-28 h-28 p-3 rounded-full bg-white shadow-md flex items-center justify-center overflow-hidden">
            <img 
              src={getAppLogo(app.name)} 
              alt={app.name} 
              className="w-28 h-28 object-contain" 
            />
          </div>
          
          {/* Statut de connexion */}
          <div className="absolute top-4 right-4">
            <span className={`flex items-center gap-1 px-2 pt-0.5 pb-0.5 rounded-full text-[11px] font-medium ${
              app.status === "true" ? "bg-green-600 text-white" : "bg-red-500 text-white"
            }`}>
              <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
              {app.status === "true" ? "Connecté" : "Non connecté"}
            </span>
          </div>
        </div>

        <div className="p-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{app.name}</h3>

          {/* Informations de la boutique si connectée */}
          {app.status === "true" && (
            <div className="space-y-3 mb-4">
              {app.store_domain && (
                <div className="flex items-center text-xs">
                  <span className="text-gray-500 w-20">Boutique :</span>
                  <span className="font-medium text-gray-700">
                    {app.store_domain}
                  </span>
                </div>
              )}
              <div className="flex items-start text-xs">
                <span className="text-gray-500 w-20 flex-shrink-0">Token :</span>
                <span className="font-mono text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded break-all">
                  {app.access_token ? maskHalf(app.access_token) : <span className="text-gray-400 italic font-normal">Aucun</span>}
                </span>
              </div>
            </div>
          )}

          {/* Saisie du domaine pour Shopify */}
          {app.name === 'Shopify' && app.status !== "true" && showShopInput && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Entrez le domaine de votre boutique Shopify
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shopDomain}
                  onChange={(e) => setShopDomain(e.target.value)}
                  placeholder="Ex : maboutique ou mabutique.myshopify.com"
                  className="flex-1 outline-none px-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                  disabled={isLoading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && shopDomain.trim()) {
                      initiateOAuth();
                    }
                  }}
                />
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => {
                    setShowShopInput(false);
                    setShopDomain('');
                  }}
                  className="px-2 py-1.5 text-xs text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors duration-300"
                  disabled={isLoading}
                >
                  Annuler
                </button>
                <button
                  onClick={initiateOAuth}
                  disabled={isLoading || !shopDomain.trim()}
                  className="flex items-center gap-1 cursor-pointer text-xs py-1 px-2 bg-green-700 text-white rounded-lg shadow-sm hover:bg-green-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <>
                      <Link className="w-3.5 h-3.5 text-white" />
                      Connecter
                    </>
                  )}
                </button>
              </div>
              <p className="text-[10px] text-gray-500 mt-2">
                Ceci est requis une seule fois. Shopify enverra automatiquement les informations de votre boutique après autorisation.
              </p>
            </div>
          )}

          {/* Boutons d'action */}
          {app.status !== "true" && !showShopInput && (
            <div className="flex justify-end gap-2">
              <button
                onClick={handleConnect}
                className="flex items-center gap-1 cursor-pointer text-xs py-1.5 px-3 bg-green-700 text-white rounded-md shadow-sm hover:bg-green-800 transition-colors duration-300"
                disabled={isLoading}
              >
                <Link className="w-3.5 h-3.5 text-white" />
                Connecter
              </button>
            </div>
          )}

          {app.status === "true" && (app.name === 'Shopify' || app.name === 'YouCan') && (
            <div className="flex justify-end gap-2">
              <button
                onClick={openSendProductsModal}
                className="flex items-center gap-1 cursor-pointer text-xs py-1.5 px-3 bg-[var(--color-primary-500)] text-white rounded-md shadow-sm hover:bg-[var(--color-primary-400)] transition-colors duration-300"
                disabled={isLoading}
              >
                <Upload className="w-3.5 h-3.5 text-white" />
                Send Products
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal des identifiants WooCommerce */}
      {showWooModal && wooCredentials && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-[99] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <img src={Woocommerce} alt="WooCommerce" className="w-8 h-8 object-contain" />
                  Connecter WooCommerce
                </h2>
                <button 
                  onClick={() => setShowWooModal(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl cursor-pointer"
                >
                  &times;
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-800 mb-2">Étape 1 : Télécharger le plugin</h3>
                  <p className="text-xs text-blue-700 mb-3">
                    Téléchargez et installez le plugin WordPress sur votre boutique WooCommerce.
                  </p>
                  <button 
                    onClick={() => {
                      try {
                        const byteCharacters = atob(wooCredentials.plugin_file_data);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                          byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: 'application/zip' });
                        
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = wooCredentials.plugin_file_name || 'woocommerce-plugin.zip';
                        document.body.appendChild(link);
                        link.click();
                        
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(link);
                      } catch (e) {
                        console.error("Download error:", e);
                        toast.error("Échec du téléchargement du fichier");
                      }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition w-full justify-center cursor-pointer"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Télécharger le plugin
                  </button>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Étape 2 : Configurer le plugin</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Clé API</label>
                      <div className="flex gap-2">
                        <code className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-xs font-mono break-all text-gray-700">
                          {wooCredentials.credentials.api_key}
                        </code>
                        <button 
                          onClick={() => copyToClipboard(wooCredentials.credentials.api_key)}
                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs transition"
                        >
                          Copier
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Secret API</label>
                      <div className="flex gap-2">
                        <code className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-xs font-mono break-all text-gray-700">
                          {wooCredentials.credentials.api_secret}
                        </code>
                        <button 
                          onClick={() => copyToClipboard(wooCredentials.credentials.api_secret)}
                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs transition"
                        >
                          Copier
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Point de terminaison Webhook</label>
                      <div className="flex gap-2">
                        <code className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-xs font-mono break-all text-gray-700">
                          {wooCredentials.credentials.endpoint}
                        </code>
                        <button 
                          onClick={() => copyToClipboard(wooCredentials.credentials.endpoint)}
                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs transition"
                        >
                          Copier
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button 
                  onClick={() => setShowWooModal(false)}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900 transition"
                >
                  Terminé
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSendProductsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[99] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl">
            <div className="p-6">
              <div className="relative flex gap-5 h-full items-center justify-between rounded-3xl px-4 py-1 mb-4 bg-gradient-to-br from-[var(--color-primary-50)] to-[var(--color-primary-50)]">
                <div className="flex items-center gap-2">
                  <div className="">
                    <img 
                      src={getAppLogo(app.name)} 
                      alt={app.name} 
                      className="w-28 h-28 object-contain" 
                    />
                    </div>
                    <div>
                      <div className="flex justify-between items-center">
                        <h2 className="text-2xl font-bold text-gray-800">Send Products to {app.name}</h2>
                      </div>

                      <p className="text-xs text-gray-500 mt-1">
                        SKU produit: NEXT-idproduct • SKU variante: VAR-idproduct-idgroup
                      </p>
                  </div>
                </div>
                <div className="absolute top-1 right-4">
                  <button
                    onClick={() => {
                      if (productSearchDebounceRef.current) {
                        clearTimeout(productSearchDebounceRef.current);
                      }
                      setShowSendProductsModal(false);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-3xl cursor-pointer"
                    disabled={isSyncingProducts}
                  >
                    &times;
                  </button>
                </div>
              </div>

              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => {
                    const next = e.target.value;
                    setProductSearch(next);
                    setProductPage(1);

                    if (productSearchDebounceRef.current) {
                      clearTimeout(productSearchDebounceRef.current);
                    }

                    // Apply search immediately while typing (debounced).
                    productSearchDebounceRef.current = setTimeout(() => {
                      fetchProductsForSync(next, 1);
                    }, 350);
                  }}
                  placeholder="Rechercher un produit..."
                  className="flex-1 outline-none px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary-500)] focus:border-transparent"
                />
                <button
                  onClick={() => {
                    if (productSearchDebounceRef.current) {
                      clearTimeout(productSearchDebounceRef.current);
                    }
                    fetchProductsForSync(productSearch, 1);
                  }}
                  className="px-3 py-2 text-xs hidden bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  disabled={isLoadingProducts}
                >
                  Rechercher
                </button>
              </div>

              <div className="grid grid-cols-6 gap-2 p-2 border border-gray-200 rounded-lg max-h-[52vh] overflow-y-auto">
                {isLoadingProducts ? (
                  <div className="col-span-6 flex justify-center items-center h-[40vh]">
                    <div className="h-6 w-6 border-t-transparent border-solid animate-spin rounded-full border-gray-900 border-[3px]"></div>
                  </div>
                ) : availableProducts.length === 0 ? (
                  <div className="p-4 text-xs text-gray-500">Aucun produit trouvé.</div>
                ) : (
                  availableProducts.map((product) => (
                    <label
                      key={product.id}
                      className={`
                        ${selectedProductIds.includes(product.id) ? 'bg-green-100 border border-green-500' : 'border border-transparent' }
                         justify-center items-center rounded-xl gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-green-100`}
                    >
                      <div className="w-20 h-20 mx-auto rounded-xl overflow-hidden">
                        <img 
                          src={`${BASE_URL}/storage/${product.image_py}`} 
                          className="w-20 h-20 object-cover object-center"
                          alt="product"
                        />
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedProductIds.includes(product.id)}
                        onChange={() => toggleProductSelection(product.id)}
                        className="w-4 h-4 hidden"
                      />
                      <div className="min-w-0 text-center mt-1">
                        <p className="text-[10px] font-medium text-gray-800 truncate">
                          {product.name}
                        </p>
                        <p className="text-[9px] text-gray-500 truncate">
                          {product.category?.name || 'No category'}
                        </p>
                      </div>
                    </label>
                  ))
                )}
              </div>

              <div className="mt-4 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {selectedProductIds.length} produit(s) sélectionné(s)
                </p>

                {/* Pagination by numbers */}
              {productLastPage > 1 && (
                <div className="flex justify-center mt-2 mb-4">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleProductPageChange(productPage - 1)}
                      disabled={isLoadingProducts || productPage === 1}
                      className={`px-2 py-1.5 cursor-pointer rounded-lg text-xs font-medium transition-all duration-200 ${
                        productPage === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-[var(--color-primary-500)] hover:text-white border border-gray-200"
                      }`}
                    >
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>

                    {getVisibleProductPages().map((page, index) => (
                      <button
                        key={index}
                        onClick={() => handleProductPageChange(page)}
                        disabled={
                          typeof page !== "number" || isLoadingProducts
                        }
                        className={`px-2.5 py-1 cursor-pointer rounded-lg text-xs font-medium transition-all duration-200 ${
                          page === productPage
                            ? "bg-[var(--color-primary-500)] text-white"
                            : typeof page === "number"
                              ? "bg-white text-gray-700 hover:bg-[var(--color-primary-500)] hover:text-white border border-gray-200"
                              : "text-gray-400 cursor-default"
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      onClick={() => handleProductPageChange(productPage + 1)}
                      disabled={
                        isLoadingProducts || productPage === productLastPage
                      }
                      className={`px-2 py-1.5 cursor-pointer rounded-lg text-xs font-medium transition-all duration-200 ${
                        productPage === productLastPage
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-[var(--color-primary-500)] hover:text-white border border-gray-200"
                      }`}
                    >
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (productSearchDebounceRef.current) {
                        clearTimeout(productSearchDebounceRef.current);
                      }
                      setShowSendProductsModal(false);
                    }}
                    className="py-1.5 px-3 text-xs cursor-pointer text-gray-600 bg-gray-200 hover:text-gray-800 rounded-md shadow-sm font-medium"
                    disabled={isSyncingProducts}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSyncSelectedProducts}
                    disabled={isSyncingProducts || selectedProductIds.length === 0}
                    className="flex items-center gap-1 cursor-pointer text-xs py-1.5 px-3 bg-[var(--color-primary-500)] text-white rounded-md shadow-sm hover:bg-[var(--color-primary-600)] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSyncingProducts ?             
                       <div className="h-4 w-4 border-t-transparent border-solid animate-spin rounded-full border-white border-[3px]"></div>
                     : 'Send Products'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}